import urlJoin from 'url-join';
import { PLATFORM_HOME_PRODUCT_MENU_ITEM, PRODUCT_MENU_ITEMS, ProductAPIMap, REPORTING_PRODUCTS } from '../constants';
import { FlagSet } from '../feature-flags';
import { APIMenuDefinition, PLATFORM_SLUG, ProductMenuItem, SECRETS_SLUG, SiteAccessibility, SiteData } from '../types';

/**
 * Shared menu-resolution selectors.
 *
 * Pure functions extracted so the legacy and modernized navigation trees resolve products and
 * the current product through the SAME logic instead of drifting apart with duplicate copies.
 * Behavior is identical to the previous inline implementation in `index.ts`.
 * `resolveFirstLevelExtras` applies the no-admin-product gate itself.
 */

/** The products a user can navigate, derived from the entitled products on their site. */
export function resolveProductMenuItems(site: SiteData): ProductMenuItem[] {
  const siteScopeNames = site.products.map((product) => product.name.toLowerCase());
  return PRODUCT_MENU_ITEMS.filter((item) => siteScopeNames.includes(item.scopeName));
}

/** Visibility of the non-product first-level navigation entries. */
export interface FirstLevelExtras {
  showReporting: boolean;
  showAutomation: boolean;
  showPathfinderAi: boolean;
  showPhantomLabs: boolean;
  /** The GLOBAL site the Administration entry switches to, when one exists. */
  adminSite?: SiteAccessibility;
  showAdminLink: boolean;
  /** Non-entitled products advertised under "More Products", in catalog order. */
  moreProducts: ProductMenuItem[];
}

/**
 * Gates for the first-level entries that aren't entitled products — one source of
 * truth for the overlay menu and the sidebar, so the two navigations can't drift.
 */
export function resolveFirstLevelExtras(
  products: ProductMenuItem[],
  flags: FlagSet,
  sites: SiteAccessibility[] | undefined,
  currentSiteData: SiteData | undefined,
): FirstLevelExtras {
  const hasAdminProduct = products.some((product) => product.isAdminApp);
  const adminSite = sites?.find((site) => site.residency === 'GLOBAL');
  return {
    showReporting: !hasAdminProduct && products.some((product) => REPORTING_PRODUCTS.includes(product.slug)),
    showAutomation: !hasAdminProduct && !!flags['platform-workflow'],
    showPathfinderAi: currentSiteData?.residency?.toUpperCase() === 'US',
    showPhantomLabs: !!flags['menu-phantom-blog'],
    adminSite,
    showAdminLink: adminSite != undefined && !!flags['pf-admin-menu-link'] && !hasAdminProduct,
    // Catalog products the site is NOT entitled to, linking to their info pages.
    // Secrets is advertised only behind its flag, and an entry without an info
    // URL has nowhere to link, so it is never advertised.
    moreProducts: hasAdminProduct
      ? []
      : PRODUCT_MENU_ITEMS.filter(
          (product) =>
            !product.isAdminApp &&
            product.productInformationUrl !== '' &&
            // By slug, not identity: entitled products happen to share references
            // with the catalog today, but nothing should depend on that.
            !products.some((entitled) => entitled.slug === product.slug) &&
            (product.slug !== SECRETS_SLUG || !!flags['pf-show-secrets']),
        ),
  };
}

/**
 * Resolve the current product from the caller-supplied pathname. The first candidate whose
 * `baseHref` prefixes the pathname wins (the platform-home item is skipped so a real product
 * matches); falls back to the platform-home item.
 */
export function resolveCurrentProduct(candidates: ProductMenuItem[], pathname: string): ProductMenuItem {
  return (
    candidates.find(
      (product) =>
        product.slug !== PLATFORM_SLUG &&
        [product.baseHref, ...(product.altBaseHrefs ?? [])].some((href) => pathname.startsWith(href)),
    ) ?? PLATFORM_HOME_PRODUCT_MENU_ITEM
  );
}

/** One crumb in a breadcrumb trail. `href` absent = not a link; `current` = the current page. */
export interface BreadcrumbTrailItem {
  name: string;
  id?: string;
  href?: string;
  current?: boolean;
}

/** Strip query/hash and trailing slashes so link and location paths compare equal. */
function normalizePath(path: string): string {
  return path.split(/[?#]/)[0].replace(/\/+$/, '');
}

/**
 * Infer a breadcrumb trail for the current product from its API menu definition,
 * matching the current pathname to a leaf item. The tree is at most two levels
 * deep (product → feature → item), so the trail is at most three crumbs:
 * product (linked to its baseHref) → section (only when the matched feature is
 * named; linked when it has a link) → item (current). Returns `[]` when nothing
 * matches so the caller can fall back to today's single-label display.
 */
export function computeBreadcrumbTrail(
  currentProduct: ProductMenuItem,
  menuDefinition: APIMenuDefinition | undefined,
  pathname: string,
): BreadcrumbTrailItem[] {
  const baseURL = ProductAPIMap[currentProduct.slug]?.baseURL ?? '/';
  const currentPath = normalizePath(pathname);
  const productCrumb: BreadcrumbTrailItem = {
    name: currentProduct.name,
    id: currentProduct.slug,
    href: currentProduct.baseHref,
  };

  // A leaf item is the most specific match: Product › Section? › Item.
  for (const feature of menuDefinition?.features ?? []) {
    const item = feature.items?.find((entry) => {
      const itemHref = entry.absolutePath ? urlJoin('/', entry.link) : urlJoin(baseURL, entry.link);
      return normalizePath(itemHref) === currentPath;
    });
    if (!item) {
      continue;
    }
    const trail: BreadcrumbTrailItem[] = [productCrumb];
    if (feature.name != undefined) {
      const featureHref = feature.absolutePath
        ? urlJoin('/', feature.link || '')
        : urlJoin(baseURL, feature.link || '');
      trail.push({
        name: feature.name,
        id: feature.id,
        ...(feature.link != undefined ? { href: featureHref } : {}),
      });
    }
    trail.push({ name: item.name, id: item.id, current: true });
    return trail;
  }

  // No leaf matched; a named, link-only feature may itself be the current page: Product › Feature.
  for (const feature of menuDefinition?.features ?? []) {
    if (feature.name != undefined && feature.link != undefined) {
      const featureHref = feature.absolutePath ? urlJoin('/', feature.link) : urlJoin(baseURL, feature.link);
      if (normalizePath(featureHref) === currentPath) {
        return [productCrumb, { name: feature.name, id: feature.id, current: true }];
      }
    }
  }

  return [];
}
